import { CopyIdButton, TableActionMenu, type TableActionItem } from '@/components/shared';
import { useMediaQuery } from '@/lib/use-media-query';
import { useNavigate } from 'react-router-dom';
import type { BackofficeVoucher, VoucherRedemptionMode } from '../types';
import { VoucherStatusChip } from './VoucherStatusChip';

export interface VouchersTableProps {
  vouchers: BackofficeVoucher[];
}

export interface VouchersTableHeaderProps {
  loading: boolean;
  total: number;
  actions?: TableActionItem[];
}

function formatDate(value: string | null): string {
  if (!value) return 'No expiry';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }).format(new Date(value));
}

function formatShortId(value: string): string {
  const [firstPart] = value.split('-');
  return firstPart || value;
}

function formatMode(value: VoucherRedemptionMode): string {
  if (value === 'single_use') return 'Single use';
  if (value === 'multi_use_capped') return 'Capped multi-use';
  return 'Uncapped multi-use';
}

function formatUsage(voucher: BackofficeVoucher): string {
  return `${voucher.confirmedRedemptions}${voucher.maxRedemptions ? ` / ${voucher.maxRedemptions}` : ''}`;
}

export function VouchersTableHeader({ loading, total, actions = [] }: VouchersTableHeaderProps) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-200 bg-neutral-50 px-5 py-4">
      <div className="text-small font-medium text-neutral-500">
        {loading ? 'Loading vouchers' : `${total} vouchers`}
      </div>
      {actions.length > 0 ? (
        <TableActionMenu actions={actions} label="Voucher table actions" />
      ) : null}
    </div>
  );
}

export function VouchersTable({ vouchers }: VouchersTableProps) {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const navigate = useNavigate();

  function openVoucher(voucherId: string) {
    navigate(`/vouchers/${voucherId}`);
  }

  if (isMobile) {
    return (
      <div className="divide-y divide-neutral-100">
        {vouchers.map((voucher) => (
          <article
            key={voucher.id}
            tabIndex={0}
            role="button"
            className="cursor-pointer px-4 py-4 focus:outline-none focus:ring-2 focus:ring-backoffice-500"
            onClick={() => openVoucher(voucher.id)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                openVoucher(voucher.id);
              }
            }}
          >
            <div className="flex min-w-0 items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="truncate text-body font-semibold text-neutral-900">
                  {voucher.name}
                </h2>
                <div className="mt-1 font-mono text-small text-neutral-500">
                  {voucher.codeDisplay}
                </div>
              </div>
              <div className="shrink-0">
                <VoucherStatusChip state={voucher.calculatedState} />
              </div>
            </div>
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-small">
              <div>
                <dt className="text-neutral-500">ID</dt>
                <dd className="flex items-center gap-1 font-mono font-semibold text-neutral-900">
                  <span>{formatShortId(voucher.id)}</span>
                  <CopyIdButton value={voucher.id} label="Copy voucher ID" />
                </dd>
              </div>
              <div>
                <dt className="text-neutral-500">Interviews</dt>
                <dd className="font-semibold text-neutral-900">{voucher.interviewQuantity}</dd>
              </div>
              <div>
                <dt className="text-neutral-500">Usage</dt>
                <dd className="font-semibold text-neutral-900">{formatUsage(voucher)}</dd>
              </div>
              <div>
                <dt className="text-neutral-500">Redemption</dt>
                <dd className="font-semibold text-neutral-900">
                  {formatMode(voucher.redemptionMode)}
                </dd>
              </div>
              <div>
                <dt className="text-neutral-500">Expires</dt>
                <dd className="font-semibold text-neutral-900">{formatDate(voucher.expiresAt)}</dd>
              </div>
            </dl>
          </article>
        ))}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[1220px] border-collapse text-left">
        <thead className="border-b border-neutral-200 bg-white text-small text-neutral-500">
          <tr>
            <th scope="col" className="min-w-[140px] px-4 py-3 font-semibold text-neutral-600">
              ID
            </th>
            <th scope="col" className="min-w-[150px] px-4 py-3 font-semibold text-neutral-600">
              Code
            </th>
            <th scope="col" className="min-w-[240px] px-4 py-3 font-semibold text-neutral-600">
              Customer facing name
            </th>
            <th scope="col" className="min-w-[140px] px-4 py-3 font-semibold text-neutral-600">
              Status
            </th>
            <th scope="col" className="min-w-[120px] px-4 py-3 font-semibold text-neutral-600">
              Interviews
            </th>
            <th scope="col" className="min-w-[220px] px-4 py-3 font-semibold text-neutral-600">
              Usage
            </th>
            <th scope="col" className="min-w-[150px] px-4 py-3 font-semibold text-neutral-600">
              Expires
            </th>
            <th scope="col" className="min-w-[220px] px-4 py-3 font-semibold text-neutral-600">
              Created by
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100 text-body">
          {vouchers.map((voucher) => (
            <tr
              key={voucher.id}
              tabIndex={0}
              className="cursor-pointer hover:bg-backoffice-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-backoffice-500"
              onClick={() => openVoucher(voucher.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openVoucher(voucher.id);
                }
              }}
            >
              <td className="px-4 py-3">
                <div className="flex items-center gap-2 font-mono text-small text-neutral-700">
                  <span>{formatShortId(voucher.id)}</span>
                  <CopyIdButton value={voucher.id} label="Copy voucher ID" />
                </div>
              </td>
              <td className="px-4 py-3 font-mono text-small text-neutral-700">
                {voucher.codeDisplay}
              </td>
              <td className="px-4 py-3 font-semibold text-neutral-900">{voucher.name}</td>
              <td className="px-4 py-3">
                <VoucherStatusChip state={voucher.calculatedState} />
              </td>
              <td className="px-4 py-3 text-neutral-700">{voucher.interviewQuantity}</td>
              <td className="px-4 py-3 text-neutral-700">
                {formatUsage(voucher)}
                <span className="ml-2 text-neutral-500">{formatMode(voucher.redemptionMode)}</span>
              </td>
              <td className="px-4 py-3 text-neutral-700">{formatDate(voucher.expiresAt)}</td>
              <td className="px-4 py-3 text-neutral-700">{voucher.createdByEmail ?? 'Unknown'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
